import React, { useState, useEffect, useCallback } from 'react';
import { listQueue, runWorker, recentFilings, dismissAllFiled } from '../lib/fax-api';
import { FaxReviewItem, FaxSignaturePendingCard, FaxRefillPendingCard, FaxFormFillCard } from '../components/fax-review-item.jsx';
import { FaxFormFillScreen } from '../components/fax-form-fill-screen.jsx';
import { FaxFiledItem } from '../components/fax-filed-item.jsx';
import { FaxModeSwitch } from '../components/fax-mode-switch.jsx';
import { FaxPrefillDefaultsPanel } from '../components/fax-prefill-defaults-panel.jsx';

export function FaxInboxPage({ currentUser }) {
  const [items, setItems] = useState(null);
  const [filings, setFilings] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [clearing, setClearing] = useState(false);
  // The currently-open form-fill screen (groupId), or null.
  const [formFillScreen, setFormFillScreen] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [q, f] = await Promise.all([listQueue(), recentFilings(7)]);
      setItems(q);
      setFilings(f);
      setError(null);
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function runNow() {
    setRunning(true);
    try { await runWorker(); await reload(); }
    catch (e) { setError(e.message); }
    finally { setRunning(false); }
  }

  async function clearAll() {
    if (!filings || filings.length === 0) return;
    const yes = window.confirm(
      `Hide all ${filings.length} item${filings.length === 1 ? '' : 's'} from Recent Activity?\n\n` +
      `The filed documents remain in Elation — this only clears the CCM list.`
    );
    if (!yes) return;
    setClearing(true); setError(null);
    try { await dismissAllFiled(7); await reload(); }
    catch (e) { setError(e.message); }
    finally { setClearing(false); }
  }

  const hasFilings = filings && filings.length > 0;

  return (
    <section className="fax-inbox-page" role="region" aria-label="Fax Inbox">
      <header className="fax-inbox-head">
        <h1 className="fax-inbox-title">
          Fax Inbox{items ? ` — ${items.length} needs review` : ''}
        </h1>
        <button className="btn sm" disabled={running} onClick={runNow}>
          {running ? 'Running…' : 'Run worker now'}
        </button>
      </header>
      <FaxModeSwitch onChange={() => reload()} />
      <FaxPrefillDefaultsPanel />
      {error && <div className="fax-inbox-error">Error: {error}</div>}

      <h2 className="fax-inbox-section-heading">Needs review</h2>
      {items === null && !error && <div className="fax-inbox-loading">Loading…</div>}
      {items && items.length === 0 && <div className="fax-inbox-empty">Review queue is clear.</div>}
      {items && items.length > 0 && (
        <div className="fax-inbox-list">
          {items.map(it => (
            it.suggested_classification === 'rx_refill_pending'
              ? <FaxRefillPendingCard key={it.elation_group_id} item={it} onChange={reload} />
              : (it.suggested_classification === 'fillable_form'
                ? <FaxFormFillCard key={it.elation_group_id} item={it} onChange={reload} onOpenScreen={setFormFillScreen} />
                : (it.suggested_classification === 'signature_request'
                  ? <FaxSignaturePendingCard key={it.elation_group_id} item={it} onChange={reload} currentUser={currentUser} />
                  : <FaxReviewItem key={it.elation_group_id} item={it} onChange={reload} />))
          ))}
        </div>
      )}

      {formFillScreen && (
        <FaxFormFillScreen
          groupId={formFillScreen}
          onClose={() => setFormFillScreen(null)}
          onDone={() => { setFormFillScreen(null); reload(); }}
        />
      )}

      <div className="fax-inbox-section-head-with-action">
        <h2 className="fax-inbox-section-heading">
          Recent activity (last 7 days){filings ? ` — ${filings.length}` : ''}
        </h2>
        {hasFilings && (
          <button className="btn ghost sm" disabled={clearing} onClick={clearAll}>
            {clearing ? 'Clearing…' : 'Clear all'}
          </button>
        )}
      </div>
      {filings === null && !error && <div className="fax-inbox-loading">Loading…</div>}
      {filings && filings.length === 0 && <div className="fax-inbox-empty">No activity in the past 7 days.</div>}
      {hasFilings && (
        <div className="fax-filed-list">
          {filings.map(f => <FaxFiledItem key={f.elation_group_id} item={f} onChange={reload} />)}
        </div>
      )}
    </section>
  );
}
